import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import Portada from '../componentes/Portada'
import { formatearBytes } from '../lib/almacen'
import { descargarCapitulo } from '../lib/offline'
import { planificar, type Plan } from '../lib/viaje'

const HORAS = [1, 2, 3, 5, 8, 12]

interface Bajando {
  hechos: number
  total: number
  titulo: string
}

/**
 * Preparar un viaje sin conexion.
 *
 * En vez de ir serie por serie marcando capitulos, se dice cuanto tiempo se
 * va a estar sin red y la app decide que llevarse.
 */
export default function Viaje() {
  const clienteConsultas = useQueryClient()
  const [horas, setHoras] = useState(5)
  const [plan, setPlan] = useState<Plan | null>(null)
  const [calculando, setCalculando] = useState(false)
  const [bajando, setBajando] = useState<Bajando | null>(null)
  const [listo, setListo] = useState(0)
  const [fallo, setFallo] = useState<string | null>(null)

  async function preparar() {
    setCalculando(true)
    setFallo(null)
    setListo(0)
    try {
      setPlan(await planificar(horas))
    } catch (err) {
      setFallo(err instanceof Error ? err.message : 'No se pudo preparar el viaje')
      setPlan(null)
    } finally {
      setCalculando(false)
    }
  }

  async function bajar() {
    if (!plan) return
    const total = plan.capitulos.length
    let hechos = 0
    let fallados = 0

    for (const capitulo of plan.capitulos) {
      setBajando({ hechos, total, titulo: `${capitulo.mangaTitulo} · ${capitulo.nombre}` })
      try {
        await descargarCapitulo(capitulo.id)
      } catch {
        // Una fuente caida no debe tumbar el viaje entero: se sigue con el
        // resto y al final se dice cuantos quedaron fuera.
        fallados++
      }
      hechos++
    }

    setBajando(null)
    setListo(total - fallados)
    setPlan(null)
    void clienteConsultas.invalidateQueries({ queryKey: ['descargasLocales'] })
  }

  if (bajando) {
    const porcentaje = Math.round((bajando.hechos / bajando.total) * 100)
    return (
      <>
        <h1>Preparando el viaje</h1>
        <p className="subtitulo">
          {bajando.hechos} de {bajando.total} capitulos
        </p>
        <div className="progreso">
          <div style={{ width: `${porcentaje}%` }} />
        </div>
        <p className="meta">{bajando.titulo}</p>
        <p className="meta" style={{ marginTop: 18 }}>
          No cierres Yomou hasta que termine.
        </p>
      </>
    )
  }

  return (
    <>
      <h1>Me voy sin internet</h1>
      <p className="subtitulo">Dime cuanto tiempo y te preparo la maleta</p>

      {listo > 0 && (
        <div className="aviso">
          <strong>Listo, {listo} capitulos en el movil</strong>
          <p className="meta">
            Ya puedes quedarte sin cobertura. <Link to="/descargas">Ver lo descargado</Link>
          </p>
        </div>
      )}

      <div className="pestanas">
        {HORAS.map((h) => (
          <button
            key={h}
            className={h === horas ? 'pestana activa' : 'pestana'}
            onClick={() => {
              setHoras(h)
              setPlan(null)
            }}
          >
            {h} h
          </button>
        ))}
      </div>

      <button className="boton" onClick={preparar} disabled={calculando} style={{ width: '100%' }}>
        {calculando ? 'Mirando que te falta…' : 'Preparar maleta'}
      </button>

      {fallo && <p className="error-texto">{fallo}</p>}

      {plan && plan.capitulos.length === 0 && (
        <div className="aviso" style={{ marginTop: 18 }}>
          <strong>No hay nada que llevarse</strong>
          <p className="meta">
            O ya lo tienes todo en el movil, o no te quedan capitulos sin leer. Actualiza la
            biblioteca desde el boton ↻ por si hay novedades.
          </p>
        </div>
      )}

      {plan && plan.capitulos.length > 0 && (
        <>
          <div className="aviso" style={{ marginTop: 18 }}>
            <strong>
              {plan.capitulos.length} capitulos · {formatearBytes(plan.bytesEstimados)}
            </strong>
            <p className="meta">
              De {plan.grupos.length} {plan.grupos.length === 1 ? 'serie' : 'series'}, las que has
              leido mas recientemente.
              {plan.seriesFuera > 0 &&
                ` Otras ${plan.seriesFuera} con pendientes se quedan fuera; pide mas horas para incluirlas.`}
            </p>
            <p className="meta">El tamano es una estimacion a partir de lo que ya te has bajado.</p>
          </div>

          <button className="boton" onClick={bajar} style={{ width: '100%' }}>
            Descargar al movil
          </button>

          {plan.grupos.map((grupo) => (
            <section key={grupo.mangaId} className="serie-descargada">
              <div className="cabecera-serie">
                <div style={{ width: 46, flexShrink: 0 }}>
                  <Portada mangaId={grupo.mangaId} ruta={grupo.portada} titulo={grupo.titulo} />
                </div>
                <div style={{ flex: 1 }}>
                  <strong>{grupo.titulo}</strong>
                  <span className="meta">
                    {grupo.capitulos.length}{' '}
                    {grupo.capitulos.length === 1 ? 'capitulo' : 'capitulos'}
                  </span>
                </div>
              </div>
              <ul className="lista">
                {grupo.capitulos.map((capitulo) => (
                  <li key={capitulo.id}>
                    <span style={{ flex: 1, padding: '10px 0' }}>{capitulo.nombre}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </>
  )
}
